import React, { useState } from 'react';
import { Box, Flex, Link, Text } from 'rebass/styled-components';
import { Button, Loader } from '@getro/rombo';
import { Plus } from 'lucide-react';

import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import { trackEvent } from '../../../helpers/analytics';
import usePageHook from '../../../hooks/usePageHook';
import { useTierGating } from '../../../hooks/useTierGating';
import { useGetApiKeysQuery, useCreateApiKeyMutation, useRevokeApiKeyMutation } from '../../../services/apiKeysV2';
import { ModalCreateApiKey } from './components/modalCreateApiKey';
import { ModalApiKeyCreated } from './components/modalApiKeyCreated';
import { ModalRevokeApiKey } from './components/modalRevokeApiKey';
import { ApiKeysDataTable } from './components/ApiKeysDataTable';

const pageTitle = 'API keys';

export const ApiKeys = () => {
  const { isCustomer } = useTierGating();
  const { data: apiKeys = [], isLoading: isLoadingApiKeys } = useGetApiKeysQuery(undefined, {
    skip: !isCustomer,
  });
  const [createApiKey, { isLoading: isCreating }] = useCreateApiKeyMutation();
  const [revokeApiKey, { isLoading: isRevoking }] = useRevokeApiKeyMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [apiKeyToRevoke, setApiKeyToRevoke] = useState(null);

  usePageHook(pageTitle);

  const onCreateApiKey = async (values, { setSubmitting, setStatus }) => {
    try {
      setSubmitting(true);
      const result = await createApiKey({ label: values.label }).unwrap();
      setNewApiKey(result.token);
      setIsCreateModalOpen(false);
      setIsSuccessModalOpen(true);
      setSubmitting(false);
      trackEvent('api_key:created');
    } catch (error) {
      setSubmitting(false);
      setStatus({ error: 'Failed to create API key. Please try again.' });
    }
  };

  const onClickRevoke = (key) => {
    setApiKeyToRevoke(key);
    setIsRevokeModalOpen(true);
    trackEvent('api_key:delete_click');
  };

  const onConfirmRevoke = async () => {
    if (apiKeyToRevoke) {
      try {
        await revokeApiKey({ id: apiKeyToRevoke.id }).unwrap();
        setIsRevokeModalOpen(false);
        setApiKeyToRevoke(null);
        trackEvent('api_key:deleted');
      } catch {
        // Do nothing
      }
    }
  };

  const onCloseRevokeModal = () => {
    setIsRevokeModalOpen(false);
    setApiKeyToRevoke(null);
  };

  const onCloseModals = () => {
    setIsCreateModalOpen(false);
    setIsSuccessModalOpen(false);
    setNewApiKey(null);
  };

  const onClickCreate = () => {
    setIsCreateModalOpen(true);
    trackEvent('api_key:create_click');
  };

  if (!isCustomer) return null;

  return (
    <Box maxWidth="600px">
      <Flex flexDirection="column" sx={{ gap: '32px' }}>
        <SettingsLayoutHeader
          title={pageTitle}
          description="Automate your workflow by connecting Getro with other tools via Zapier or building your own app."
        />
        <Text fontSize="14px">
          API keys allow you to access and update the network data via Zapier or your custom app. Learn more and explore
          ideas in our{' '}
          <Link color="text.main" href="https://docs.getro.com" target="_blank" rel="noopener noreferrer">
            integration guide
          </Link>
        </Text>

        {isLoadingApiKeys ? (
          <Flex justifyContent="center" alignItems="center" py="40px">
            <Loader />
          </Flex>
        ) : (
          <>
            {apiKeys.length === 0 ? (
              <Flex flexDirection="column" alignItems="center" justifyContent="center" p="40px" sx={{ gap: '16px' }}>
                <Flex flexDirection="column" alignItems="center" justifyContent="center" sx={{ gap: '8px' }}>
                  <Text fontSize="14px" fontWeight="semibold">
                    No keys created yet
                  </Text>
                  <Text fontSize="14px">Create an API key to start integrating Getro with other tools.</Text>
                </Flex>
                <Button variant="secondary" size="small" icon={Plus} onClick={onClickCreate}>
                  Create new API key
                </Button>
              </Flex>
            ) : (
              <Flex flexDirection="column" sx={{ gap: '16px' }}>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text fontWeight="medium" color="text.dark">
                    Network API keys
                  </Text>
                  <Button variant="secondary" size="small" icon={Plus} onClick={onClickCreate}>
                    Create API key
                  </Button>
                </Flex>
                <ApiKeysDataTable apiKeys={apiKeys} onClickRevoke={onClickRevoke} />
              </Flex>
            )}
          </>
        )}
      </Flex>

      {isCreateModalOpen && (
        <ModalCreateApiKey onClose={onCloseModals} onSubmit={onCreateApiKey} isLoading={isCreating} />
      )}
      {isSuccessModalOpen && <ModalApiKeyCreated apiKey={newApiKey} onClose={onCloseModals} />}
      {isRevokeModalOpen && (
        <ModalRevokeApiKey onClose={onCloseRevokeModal} onConfirm={onConfirmRevoke} isLoading={isRevoking} />
      )}
    </Box>
  );
};
